/**
 * Firestore Real-Time Sync Layer for "What To Eat?"
 *
 * SINGLE SOURCE OF TRUTH: Firestore.
 * NO localStorage caching for shared data.
 *
 * This module sets up onSnapshot listeners for all shared collections:
 *   - menuItems
 *   - orders
 *   - clients
 *   - providers
 *   - reviews
 *   - mealRequests
 *
 * Whenever Firestore data changes (in ANY browser), listeners fire,
 * update the in-memory arrays, and re-render all UI panels instantly.
 *
 * Loaded BEFORE app.js in index.html.
 */

(function () {
  let syncStarted = false;
  let listeners = [];
  let currentAuthUser = null;
  const FALLBACK_MENU_ITEMS = [
    {
      id: 'fallback-1',
      providerId: 'fallback-provider',
      providerName: 'Local Demo Kitchen',
      foodName: 'Daily Special Bowl',
      description: 'Warm rice, grilled chicken, vegetables, and house sauce.',
      price: 12.99,
      country: 'US',
      servingsLeft: 12,
      isActive: true,
      photo: ''
    },
    {
      id: 'fallback-2',
      providerId: 'fallback-provider',
      providerName: 'Local Demo Kitchen',
      foodName: 'Garden Wrap',
      description: 'Fresh greens, hummus, roasted vegetables, and feta.',
      price: 10.5,
      country: 'US',
      servingsLeft: 8,
      isActive: true,
      photo: ''
    }
  ];

  const MODULAR = "https://www.gstatic.com/firebasejs/12.18.0";

  async function getModularDb() {
    // Prefer raw modular Firestore instance exposed by firebase-sdk-bootstrap.js
    if (window.firebaseDbRaw) return window.firebaseDbRaw;
    if (window.firebaseDbInstance) return window.firebaseDbInstance;
    return null;
  }

  async function getModularAuth() {
    if (window.firebaseAuthRaw) return window.firebaseAuthRaw;
    if (window.firebaseAuthInstance && window.firebaseAuthInstance._raw) {
      return window.firebaseAuthInstance._raw;
    }
    if (window.firebaseAuthInstance) return window.firebaseAuthInstance;
    return null;
  }

  async function waitForFirebase(timeoutMs) {
    const maxWait = timeoutMs || 15000;
    const start = Date.now();
    while (Date.now() - start < maxWait) {
      const db = await getModularDb();
      const auth = await getModularAuth();
      if (db && auth) return { db, auth };
      if (typeof window.ensureFirebaseReady === "function") {
        try { await window.ensureFirebaseReady(); } catch (e) {}
      }
      await new Promise((r) => setTimeout(r, 200));
    }
    console.warn("[FirestoreSync] Timed out waiting for Firebase");
    return null;
  }

  function hasData(list) {
    return Array.isArray(list) && list.length > 0;
  }

  function applyFallbackMenuItems() {
    const fallbackItems = Array.isArray(window.__fallbackMenuItems) && window.__fallbackMenuItems.length > 0
      ? window.__fallbackMenuItems
      : FALLBACK_MENU_ITEMS;

    if (typeof window !== 'undefined') {
      window.__fallbackMenuItems = fallbackItems;
    }

    if (typeof menuItems !== 'undefined' && Array.isArray(menuItems)) {
      menuItems.length = 0;
      fallbackItems.forEach((item) => menuItems.push(item));
    }
    if (typeof window !== 'undefined') {
      window.menuItems = typeof menuItems !== 'undefined' && Array.isArray(menuItems) ? menuItems : fallbackItems;
      window.__wtE_allMenuItems = window.menuItems;
    }
    try {
      if (typeof renderMenuList === 'function') renderMenuList();
      if (typeof renderSearchResults === 'function') renderSearchResults();
    } catch (e) {}
  }

  function triggerReRender() {
    try {
      if (typeof renderMenuList === "function") renderMenuList();
    } catch (e) {}
    try {
      if (typeof renderSearchResults === "function") renderSearchResults();
    } catch (e) {}
    try {
      if (typeof renderClientSearchResults === "function") renderClientSearchResults();
    } catch (e) {}
    try {
      if (typeof renderClientOrders === "function") renderClientOrders();
    } catch (e) {}
    try {
      if (typeof renderProviderOrders === "function") renderProviderOrders();
    } catch (e) {}
    try {
      if (typeof renderProviderReviews === "function") renderProviderReviews();
    } catch (e) {}
    try {
      if (typeof renderRequestsList === "function") renderRequestsList();
    } catch (e) {}
    try {
      if (typeof renderClientRequestsList === "function") renderClientRequestsList();
    } catch (e) {}
    try {
      if (typeof renderAdminStats === "function" && typeof isAdminLoggedIn === "function" && isAdminLoggedIn()) {
        renderAdminStats();
      }
    } catch (e) {}
    try {
      if (typeof renderAdminSubscriptions === "function" && typeof isAdminLoggedIn === "function" && isAdminLoggedIn()) {
        renderAdminSubscriptions();
      }
    } catch (e) {}
    try {
      if (typeof renderAdminClients === "function" && typeof isAdminLoggedIn === "function" && isAdminLoggedIn()) {
        renderAdminClients();
      }
    } catch (e) {}
    try {
      if (typeof renderAdminProviders === "function" && typeof isAdminLoggedIn === "function" && isAdminLoggedIn()) {
        renderAdminProviders();
      }
    } catch (e) {}
    try {
      if (typeof renderAdminOrders === "function" && typeof isAdminLoggedIn === "function" && isAdminLoggedIn()) {
        renderAdminOrders();
      }
    } catch (e) {}
    try {
      if (typeof renderAdminReviews === "function" && typeof isAdminLoggedIn === "function" && isAdminLoggedIn()) {
        renderAdminReviews();
      }
    } catch (e) {}
    try {
      if (typeof updateProviderFilter === "function") updateProviderFilter();
    } catch (e) {}
    try {
      if (typeof updateClientProviderFilter === "function") updateClientProviderFilter();
    } catch (e) {}
  }

  window.triggerAppReRender = triggerReRender;

  function stopAllListeners() {
    listeners.forEach((unsub) => {
      try { if (typeof unsub === "function") unsub(); } catch (e) {}
    });
    listeners = [];
    syncStarted = false;
  }

  window.__stopFirestoreRealtimeSync = stopAllListeners;

  /**
   * Single onSnapshot helper that updates a target array in place
   * (preserving reference) and triggers re-render.
   */
  function watchCollection(db, collectionName, targetArrayGetter, onSnapshotFns, onError) {
    if (!db) return null;
    const { collection, onSnapshot } = onSnapshotFns;
    const colRef = collection(db, collectionName);
    return onSnapshot(
      colRef,
      (snapshot) => {
        const docs = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data() || {};
          // Ensure stable id field exists for app rendering logic
          if (data.id == null) data.id = docSnap.id;
          docs.push(data);
        });
        docs.sort((a, b) => {
          const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return tb - ta;
        });
        const target = targetArrayGetter();
        if (target === undefined || target === null) return;
        target.length = 0;
        docs.forEach((d) => target.push(d));
        console.log(`[FirestoreSync] Synced ${collectionName}: ${docs.length} docs`);
        triggerReRender();
      },
      (error) => {
        console.warn(`[FirestoreSync] Listener error on ${collectionName}:`, error.code || error.message);
        if (typeof onError === "function") onError(error);
      }
    );
  }

  // Pagination state: tracks the last Firestore doc snapshot for each paginated collection
  // so "Load More" can call startAfter(cursor).
  const PAGE_SIZE = 50;
  const paginationState = {};

  async function startSync(db, auth, onSnapshotFns) {
    if (syncStarted) return;
    if (!auth || !auth.currentUser) {
      return;
    }
    syncStarted = true;
    console.log("[FirestoreSync] Starting real-time listeners on Firestore collections...");

    const { collection, onSnapshot, query, orderBy, limit, startAfter } = onSnapshotFns;

    const getWindowArray = (name) => {
      const value = typeof window !== "undefined" ? window[name] : undefined;
      return Array.isArray(value) ? value : null;
    };

    // Watch a collection with orderBy + limit (cursor-paginated).
    // append=true means add to existing array (Load More), otherwise replace.
    const watchPaged = (name, getter, append) => {
      const state = paginationState[name] || { lastDoc: null, hasMore: true };
      paginationState[name] = state;

      const constraints = [orderBy("createdAt", "desc"), limit(PAGE_SIZE)];
      if (append && state.lastDoc) constraints.push(startAfter(state.lastDoc));

      const q = query(collection(db, name), ...constraints);
      const unsub = onSnapshot(
        q,
        (snapshot) => {
          const docs = [];
          snapshot.forEach((d) => {
            const data = d.data() || {};
            if (data.id == null) data.id = d.id;
            docs.push(data);
          });

          // Track cursor for next page
          if (!snapshot.empty) {
            state.lastDoc = snapshot.docs[snapshot.docs.length - 1];
          }
          state.hasMore = docs.length >= PAGE_SIZE;

          const target = getter();
          if (target === undefined || target === null) return;
          if (name === "menuItems" && typeof window !== "undefined" && typeof window.bridgeFirestoreMenuItemsToLocal === "function") {
            window.bridgeFirestoreMenuItemsToLocal(docs);
          } else if (append) {
            // Avoid duplicates: filter out docs already in array
            const existingIds = new Set(target.map((x) => x.id));
            docs.forEach((x) => { if (!existingIds.has(x.id)) target.push(x); });
          } else {
            target.length = 0;
            docs.forEach((x) => target.push(x));
          }
          console.log(`[FirestoreSync] Synced ${name}: ${docs.length} docs (hasMore: ${state.hasMore})`);
          triggerReRender();
        },
        (error) => {
          console.warn(`[FirestoreSync] Listener error on ${name}:`, error.code || error.message);
        }
      );
      listeners.push(unsub);
    };

    // Watch a small collection with no limit (naturally bounded by user scope or small size).
    const watch = (name, getter) => {
      const unsub = onSnapshot(
        collection(db, name),
        (snapshot) => {
          const docs = [];
          snapshot.forEach((d) => {
            const data = d.data() || {};
            if (data.id == null) data.id = d.id;
            docs.push(data);
          });
          const target = getter();
          if (target === undefined || target === null) return;
          target.length = 0;
          docs.forEach((x) => target.push(x));
          console.log(`[FirestoreSync] Synced ${name}: ${docs.length} docs`);
          triggerReRender();
        },
        (error) => {
          console.warn(`[FirestoreSync] Listener error on ${name}:`, error.code || error.message);
        }
      );
      listeners.push(unsub);
    };

    let isAdminSession = false;
    try {
      const authUser = (auth && auth.currentUser) || currentAuthUser || null;
      if (authUser && typeof authUser.getIdTokenResult === "function") {
        const token = await authUser.getIdTokenResult(true);
        const claims = (token && token.claims) || {};
        const role = String(claims.role || "").toLowerCase();
        isAdminSession = claims.admin === true || role === "admin";
      }
    } catch (e) {
      console.warn("[FirestoreSync] Unable to resolve role claims for listener scoping:", e.message || e);
    }

    // menuItems - public search data with existing pagination in syncAllMenuItems; use small limit here.
    watchPaged("menuItems", () => getWindowArray("menuItems") || getWindowArray("__wtE_allMenuItems") || null, false);

    // providers - admin-level data, paginated
    watchPaged("providers", () => getWindowArray("providers") || null, false);

    // reviews - paginated
    watchPaged("reviews", () => getWindowArray("reviews") || null, false);

    // Privileged collections must be scoped by role. Non-admin sessions rely on
    // firebase-integration.js role-aware listeners to avoid permission-denied loops.
    if (isAdminSession) {
      watchPaged("orders", () => getWindowArray("orders") || getWindowArray("clientMessages") || getWindowArray("__wtE_orders") || null, false);
      watchPaged("clients", () => getWindowArray("clients") || null, false);
      watch("mealRequests", () => getWindowArray("mealRequests") || null);
    } else {
      console.log("[FirestoreSync] Non-admin session detected: skipping unscoped listeners for orders, clients, and mealRequests.");
    }

    // Expose Load More helper for UI buttons
    window.__firestoreLoadMore = async function (collectionName) {
      const state = paginationState[collectionName];
      if (!state || !state.hasMore || !state.lastDoc) {
        console.log(`[FirestoreSync] No more pages for ${collectionName}`);
        return false;
      }
      console.log(`[FirestoreSync] Loading more ${collectionName}...`);
      const getArr = (name) => {
        const v = typeof window !== "undefined" ? window[name] : undefined;
        return Array.isArray(v) ? v : null;
      };
      const getterMap = {
        orders: () => getArr("orders") || getArr("clientMessages") || getArr("__wtE_orders") || null,
        clients: () => getArr("clients") || null,
        providers: () => getArr("providers") || null,
        reviews: () => getArr("reviews") || null,
        menuItems: () => getArr("menuItems") || getArr("__wtE_allMenuItems") || null,
      };
      const getter = getterMap[collectionName];
      if (getter) watchPaged(collectionName, getter, true);
      return true;
    };

    // Expose pagination state for UI to check hasMore
    window.__firestorePaginationState = paginationState;

    console.log("[FirestoreSync] Real-time listeners established (menuItems, orders, clients, providers, reviews, mealRequests)");
  }

  async function init() {
    const fb = await waitForFirebase(20000);
    if (!fb) return;
    const { db, auth } = fb;

    try {
      const mod = await import(`${MODULAR}/firebase-firestore.js`);
      const authMod = await import(`${MODULAR}/firebase-auth.js`);

      // Register auth state -> start sync when a user is signed in.
      // Firestore rules require authentication for most collections.
      authMod.onAuthStateChanged(auth, (user) => {
        currentAuthUser = user;
        console.log("[FirestoreSync] Auth state changed. user:", user ? user.email : "null");

        if (user) {
          // Delay slightly to let getUserRole in firebase-integration complete
          setTimeout(() => {
            if (!auth.currentUser || auth.currentUser.uid !== user.uid) {
              return;
            }
            startSync(db, auth, {
              collection: mod.collection,
              onSnapshot: mod.onSnapshot,
              query: mod.query,
              orderBy: mod.orderBy,
              limit: mod.limit,
              startAfter: mod.startAfter
            });
          }, 500);
        } else {
          stopAllListeners();
          // Even unauthenticated, we can still try listening to public menuItems.
          // If rules allow public reads, this populates search results.
          if (typeof window.__menuItemsPublicRead === "function") {
            window.__menuItemsPublicRead();
          }
        }
      });

      // If Firebase already has a persisted session, the auth state listener
      // will fire immediately. For first load with no user, we still attempt
      // the public menuItems listener (rules updated to allow).
      console.log("[FirestoreSync] Module initialized");
    } catch (e) {
      console.error("[FirestoreSync] Failed to load modular SDK:", e.message);
    }
  }

  // Public helper to read menu items with cursor-based pagination and country filtering
  window.__menuItemsPublicRead = async function (options = {}) {
    try {
      const db = await getModularDb();
      if (!db) {
        console.warn("[FirestoreSync] Firebase DB unavailable for public menu read; using fallback menu data.");
        applyFallbackMenuItems();
        return { docs: Array.isArray(window.__fallbackMenuItems) ? window.__fallbackMenuItems : [], lastDoc: null };
      }
      const mod = await import(`${MODULAR}/firebase-firestore.js`);

      let selectedCountry = null;
      let pageSize = 24;
      let lastDoc = null;
      let append = false;

      if (typeof options === "string") {
        selectedCountry = options || null;
      } else if (options && typeof options === "object") {
        selectedCountry = options.country || options.selectedCountry || window.selectedCountry || null;
        pageSize = Number(options.pageSize || options.limit || 24);
        lastDoc = options.lastDoc || options.startAfter || null;
        append = Boolean(options.append);
      } else {
        selectedCountry = window.selectedCountry || null;
      }

      const queryConstraints = [];
      if (selectedCountry) {
        queryConstraints.push(mod.where("country", "==", selectedCountry));
      }
      queryConstraints.push(mod.limit(pageSize));

      if (lastDoc) {
        queryConstraints.push(mod.startAfter(lastDoc));
      }

      const q = mod.query(mod.collection(db, "menuItems"), ...queryConstraints);
      const snap = await mod.getDocs(q);

      const docs = [];
      let newLastDoc = null;
      if (!snap.empty) {
        newLastDoc = snap.docs[snap.docs.length - 1];
      }

      snap.forEach((d) => {
        const data = d.data() || {};
        if (data.id == null) data.id = d.id;
        docs.push(data);
      });

      if (typeof menuItems !== "undefined") {
        if (append && Array.isArray(menuItems)) {
          docs.forEach((x) => menuItems.push(x));
        } else {
          menuItems.length = 0;
          docs.forEach((x) => menuItems.push(x));
        }
        console.log("[FirestoreSync] Public menuItems loaded:", docs.length, "country:", selectedCountry);
        triggerReRender();
      }

      if (typeof window !== "undefined" && typeof window.bridgeFirestoreMenuItemsToLocal === "function") {
        window.bridgeFirestoreMenuItemsToLocal(docs);
      }
      window.__wtE_allMenuItems = docs;
      window.menuItems = typeof menuItems !== "undefined" ? menuItems : docs;
      return { docs, lastDoc: newLastDoc };
    } catch (e) {
      const code = e && (e.code || e.name || '');
      const message = e && (e.message || String(e));
      console.warn("[FirestoreSync] Public menuItems read failed (rules may require auth):", code || message);
      if (/permission-denied|insufficient permissions|firebase-app-check-token-is-invalid/i.test(String(code || '') + String(message || ''))) {
        console.warn("[FirestoreSync] Falling back to built-in menu data so the storefront still renders while Firebase rules/App Check are corrected.");
        applyFallbackMenuItems();
      }
    }
  };

  // Kick off
  init();
  // Also attempt public menu items early
  setTimeout(() => {
    window.__menuItemsPublicRead();
  }, 2500);
})();
